/**
 * Shared Moralis read helpers for bot UIs (stats page + optional imports).
 */
import Moralis from "moralis-v1";

const desanitizeKeys = (obj) => {
  if (typeof obj !== "object" || obj === null || Array.isArray(obj)) return obj;
  const out = {};
  for (const [key, value] of Object.entries(obj)) {
    const k = key.replace(/_DOT_/g, ".").replace(/_DOLLAR_/g, "$");
    out[k] =
      typeof value === "object" && value !== null && !Array.isArray(value)
        ? desanitizeKeys(value)
        : value;
  }
  return out;
};

export async function moralisLoadBotConfigs(player) {
  const BotConfig = Moralis.Object.extend("BotConfig");
  const q = new Moralis.Query(BotConfig);
  q.equalTo("player", String(player));
  const rows = await q.find();
  const out = {};
  rows.forEach((r) => {
    out[r.get("botKey")] = r.get("configJSON");
  });
  return out;
}

export async function moralisLoadPositions(player) {
  const BotPosition = Moralis.Object.extend("BotPosition");
  const q = new Moralis.Query(BotPosition);
  q.equalTo("player", String(player));
  const row = await q.first();
  if (!row) return {};
  const raw = row.get("positionJSON");
  try {
    const parsed = typeof raw === "string" ? JSON.parse(raw) : raw;
    return desanitizeKeys(parsed || {});
  } catch {
    return {};
  }
}

export async function moralisLoadTradeHistory(player, { limit = 200 } = {}) {
  const TradeHistory = Moralis.Object.extend("TradeHistory");
  const q = new Moralis.Query(TradeHistory);
  q.equalTo("player", String(player));
  q.descending("timestamp");
  q.limit(limit);
  const rows = await q.find();
  return rows.map((r) => ({
    ts: r.get("timestamp"),
    side: r.get("side"),
    amount: r.get("amount"),
    marketKey: r.get("marketKey"),
    token: r.get("token"),
    initiator: r.get("initiator"),
    txHash: r.get("txHash"),
  }));
}

export async function moralisLoadAllPlayerState(player) {
  const [configs, positions, history] = await Promise.all([
    moralisLoadBotConfigs(player),
    moralisLoadPositions(player),
    moralisLoadTradeHistory(player, { limit: 200 }),
  ]);
  return { configs, positions, history };
}

/** Normalize positions blob from getBotRuntimeState (single doc stored under key "undefined"). */
export function normalizeCloudPositions(positionsField) {
  if (!positionsField || typeof positionsField !== "object") return {};

  const desanitize = (obj) => desanitizeKeys(obj);

  if (positionsField.undefined && typeof positionsField.undefined === "object") {
    const inner = positionsField.undefined;
    if (inner.holdings !== undefined || inner.avgEntry !== undefined) {
      return desanitize({ "ETH-USDC": inner });
    }
    return desanitize(inner);
  }

  const out = {};
  for (const [key, value] of Object.entries(positionsField)) {
    if (!value || typeof value !== "object") continue;
    if (value.holdings !== undefined || value.avgEntry !== undefined || value.priceTargetBuy !== undefined) {
      out[key] = desanitize(value);
    } else {
      Object.assign(out, desanitize(value));
    }
  }
  return out;
}

/**
 * Public stats API — uses Parse Cloud (master key server-side). Works without wallet login.
 */
export async function fetchBotRuntimeStateFromCloud(player) {
  const serverUrl = (process.env.REACT_APP_SERVER_URL || "").replace(/\/$/, "");
  const appId = process.env.REACT_APP_APPLICATION_ID || "";
  if (!serverUrl || !appId) {
    throw new Error("Missing REACT_APP_SERVER_URL or REACT_APP_APPLICATION_ID");
  }

  const url = `${serverUrl}/functions/getBotRuntimeState`;
  const res = await fetch(url, {
    method: "POST",
    headers: {
      "X-Parse-Application-Id": appId,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ player: String(player).toLowerCase() }),
  });

  const json = await res.json().catch(() => ({}));
  if (!res.ok) {
    const msg = json?.error || json?.message || `HTTP ${res.status}`;
    throw new Error(typeof msg === "string" ? msg : JSON.stringify(msg));
  }

  const result = json.result || {};
  return {
    configs: result.configs || {},
    positions: normalizeCloudPositions(result.positions),
    history: Array.isArray(result.history) ? result.history : [],
    control: result.control || { enabled: true },
  };
}

export function loadLocalPnlData() {
  try {
    const saved = localStorage.getItem("botPnlData");
    return saved ? JSON.parse(saved) : {};
  } catch {
    return {};
  }
}
